import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';

/**
 * Every page is route-level code-split. This matters specifically for
 * Home, CncLathe, and CncMilling — they pull in 3D scene components, and
 * without this, Vite's modulepreload analysis would preload the ~940KB
 * three.js chunk on every route (since App.tsx would otherwise statically
 * import all pages up front, including the 3D ones). With lazy(), that
 * chunk is only ever requested when a user actually navigates to one of
 * those three routes.
 */
const Home = lazy(() => import('@/pages/Home'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const FloeAi = lazy(() => import('@/pages/FloeAi'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Account = lazy(() => import('@/pages/Account'));
const AccountSecurity = lazy(() => import('@/pages/AccountSecurity'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const DeleteAccount = lazy(() => import('@/pages/DeleteAccount'));
const NotFound = lazy(() => import('@/pages/NotFound'));

const ServicesIndex = lazy(() => import('@/pages/services/ServicesIndex'));
const ServiceDetail = lazy(() => import('@/pages/services/ServiceDetail'));

const WorkIndex = lazy(() => import('@/pages/work/WorkIndex'));
const WorkDetail = lazy(() => import('@/pages/work/WorkDetail'));

const CncOverview = lazy(() => import('@/pages/cnc/CncOverview'));
const CncLathe = lazy(() => import('@/pages/cnc/CncLathe'));
const CncMilling = lazy(() => import('@/pages/cnc/CncMilling'));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-line border-t-signal" />
    </div>
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Layout>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />

          <Route path="/services" element={<ServicesIndex />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />

          <Route path="/work" element={<WorkIndex />} />
          <Route path="/work/:project" element={<WorkDetail />} />

          <Route path="/cnc" element={<CncOverview />} />
          <Route path="/cnc/lathe" element={<CncLathe />} />
          <Route path="/cnc/milling" element={<CncMilling />} />

          <Route path="/floe-ai" element={<FloeAi />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/delete-account" element={<DeleteAccount />} />

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/security"
            element={
              <ProtectedRoute>
                <AccountSecurity />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </Layout>
    </>
  );
}
